/**
 * Workspace compound tool — WP-484 S2 (§3, new noun).
 *
 * Absorbs `health` (check/whoami/status/audit/self-test), `get-usage-summary`
 * → usage, and `governance-proposals` → proposals-list/proposals-respond/
 * proposals-count. This is the one genuinely NEW name in S2 — every other
 * core noun reuses an existing name (§7 S2 note). Legacy `health`,
 * `get-usage-summary`, and `governance-proposals` tools stay registered
 * alongside (§7 "alongside" mechanic) — all call the SAME handler functions,
 * one business-logic path per action.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { thinWrapper, parseOrFail, type ToolResult } from "../envelope.js";
import { runWithToolContext } from "../client.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleHealthCheck, handleWhoami, handleWorkspaceStatus, handleAudit, handleSelfTest } from "./health.js";
import { handleUsageSummary } from "./usage.js";
import { handleProposalsList, handleProposalsCount, handleProposalsRespond } from "./governance.js";

const WORKSPACE_ACTIONS = [
  "check", "whoami", "status", "audit", "self-test",
  "usage", "proposals-list", "proposals-respond", "proposals-count",
] as const;
type WorkspaceAction = (typeof WORKSPACE_ACTIONS)[number];

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const workspaceSchema = z.object({
  action: z.enum(WORKSPACE_ACTIONS).describe(
    "'check': connectivity and workspace stats (absorbs health action=check). " +
      "'whoami': session identity (absorbs health action=whoami). " +
      "'status': workspace readiness (absorbs health action=status). " +
      "'audit': session audit log (absorbs health action=audit). " +
      "'self-test': validate all tool schemas (absorbs health action=self-test). " +
      "'usage': LLM usage and cost summary (absorbs get-usage-summary). " +
      "'proposals-list': list open consent proposals (absorbs governance-proposals action=list). " +
      "'proposals-respond': approve/reject a consent proposal (absorbs governance-proposals action=respond). " +
      "'proposals-count': count open consent proposals (absorbs governance-proposals action=count).",
  ),
  limit: z.number().min(1).max(50).optional().describe("For 'audit': how many recent calls to show (max 50, default 20)."),
  periodDays: z.number().min(1).max(90).optional().describe("For 'usage': number of days to look back (default 30, max 90)."),
  status: z.enum(["open", "approved", "objected", "expired"]).optional().describe("For 'proposals-list': filter by status (default: open)."),
  proposalId: z.string().max(200).optional().describe("For 'proposals-respond': proposal ID."),
  verdict: z.enum(["approve", "reject"]).optional().describe("For 'proposals-respond': approve or reject."),
  reason: z.string().max(2000).optional().describe("For 'proposals-respond': reason for the verdict (required when rejecting)."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const workspaceCheckVariant = z.object({ action: z.literal("check") });
const workspaceWhoamiVariant = z.object({ action: z.literal("whoami") });
const workspaceStatusVariant = z.object({ action: z.literal("status") });
const workspaceAuditVariant = z.object({ action: z.literal("audit"), limit: z.number().min(1).max(50).optional().default(20) });
const workspaceSelfTestVariant = z.object({ action: z.literal("self-test") });
const workspaceUsageVariant = z.object({ action: z.literal("usage"), periodDays: z.number().min(1).max(90).optional() });
const workspaceProposalsListVariant = z.object({
  action: z.literal("proposals-list"),
  status: z.enum(["open", "approved", "objected", "expired"]).optional(),
});
const workspaceProposalsRespondVariant = z.object({
  action: z.literal("proposals-respond"),
  proposalId: z.string().max(200),
  verdict: z.enum(["approve", "reject"]),
  reason: z.string().max(2000).optional(),
});
const workspaceProposalsCountVariant = z.object({ action: z.literal("proposals-count") });

export const workspaceActionUnion = z.discriminatedUnion("action", [
  workspaceCheckVariant,
  workspaceWhoamiVariant,
  workspaceStatusVariant,
  workspaceAuditVariant,
  workspaceSelfTestVariant,
  workspaceUsageVariant,
  workspaceProposalsListVariant,
  workspaceProposalsRespondVariant,
  workspaceProposalsCountVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const WORKSPACE_ACTION_SPECS: Record<WorkspaceAction, ActionParamSpec> = {
  check: { params: [], description: "Verify connectivity; no params." },
  whoami: { params: [], description: "Session identity; no params." },
  status: { params: [], description: "Workspace readiness; no params." },
  audit: { params: ["limit"], description: "limit is optional (default 20, max 50)." },
  "self-test": { params: [], description: "Validate all tool schemas; no params." },
  usage: { params: ["periodDays"], description: "periodDays is optional (default 30, max 90)." },
  "proposals-list": { params: ["status"], description: "status is optional (default 'open')." },
  "proposals-respond": { params: ["proposalId", "verdict", "reason"], description: "proposalId and verdict are required; reason is required when rejecting." },
  "proposals-count": { params: [], description: "Count open proposals; no params." },
};

export function registerWorkspaceTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const workspaceHandlers: Record<WorkspaceAction, (data: any) => Promise<ToolResult>> = {
    check: () => handleHealthCheck(),
    whoami: () => handleWhoami(),
    status: () => handleWorkspaceStatus(),
    audit: (data) => handleAudit(data.limit ?? 20),
    "self-test": () => Promise.resolve(handleSelfTest(server)),
    usage: (data) => handleUsageSummary(data.periodDays),
    "proposals-list": (data) => handleProposalsList(data.status),
    "proposals-respond": (data) => handleProposalsRespond(data.proposalId, data.verdict, data.reason),
    "proposals-count": () => handleProposalsCount(),
  };

  server.registerTool(
    "workspace",
    {
      title: "Workspace",
      description:
        "Diagnostics, session identity, usage, and governance proposals for the current workspace. Nine actions:\n\n" +
        "- **check**: Verify connectivity — workspace, collections, entries, latency.\n" +
        "- **whoami**: Session identity — workspace ID, slug, name.\n" +
        "- **status**: Workspace readiness — score, gaps, stats (entries, relations, orphans).\n" +
        "- **audit**: Session audit log — last N backend calls with summary.\n" +
        "- **self-test**: Validate all registered tool schemas.\n" +
        "- **usage**: LLM usage and cost summary — spend, tokens, model/feature breakdown (absorbs `get-usage-summary`).\n" +
        "- **proposals-list**: List open consent proposals awaiting review (absorbs `governance-proposals` action=list).\n" +
        "- **proposals-respond**: Approve or reject a consent proposal (absorbs `governance-proposals` action=respond).\n" +
        "- **proposals-count**: Count open consent proposals for badge/notification display (absorbs `governance-proposals` action=count).",
      inputSchema: workspaceSchema,
      // Mixed read/write noun (§3 R3): proposals-respond writes; every other action reads.
      annotations: { readOnlyHint: false, idempotentHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(workspaceSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "workspace", action }, () =>
        dispatchDiscriminated("workspace", workspaceActionUnion, parsed.data, WORKSPACE_ACTION_SPECS, workspaceHandlers),
      );
    }),
  );
}
